// ProviderDetailDialog.js
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  ListItemIcon,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DisconnectIcon from "@mui/icons-material/LinkOff";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import CircleIcon from "@mui/icons-material/Circle";

export default function ProviderDetailDialog({
  open,
  provider,
  onClose,
  onDisconnect,
  onNewConnect,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!provider) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          height: isMobile ? "100%" : "80vh",
          maxHeight: "100%",
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          minHeight: 48,
        }}
      >
        <IconButton
          edge="start"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="subtitle1" component="div" sx={{ flex: 1 }}>
          Provider Details
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          p: "16px !important",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box sx={{ mb: provider.hub ? 1 : 0 }}>
            <Typography variant="h6">{provider.name}</Typography>
            {provider.hub && (
              <Chip
                label="Hub Required"
                size="small"
                color="warning"
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          {provider.multiConnect ? (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => onNewConnect(provider)}
              sx={{ textTransform: "none", boxShadow: "none" }}
            >
              New Connection
            </Button>
          ) : (
            <Button
              variant="contained"
              fullWidth
              color={provider.installed ? "error" : "primary"}
              onClick={() =>
                provider.installed
                  ? onDisconnect(provider)
                  : onNewConnect(provider)
              }
              startIcon={provider.installed ? <DisconnectIcon /> : <AddIcon />}
              sx={{ textTransform: "none", boxShadow: "none" }}
            >
              {provider.installed ? "Disconnect" : "Connect"}
            </Button>
          )}
        </Paper>

        {/* Connections List */}
        {provider.connects.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1">Active Connections</Typography>
            </Box>

            <List
              sx={{
                "& .MuiListItem-root": {
                  px: 2,
                  py: 1.5,
                },
              }}
            >
              {provider.connects.map((connection, index) => (
                <ListItem
                  key={connection.id}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="disconnect"
                      onClick={() => onDisconnect(provider, connection.id)}
                      sx={{ color: theme.palette.error.main }}
                      size="small"
                    >
                      <DisconnectIcon />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <CircleIcon
                      sx={{
                        fontSize: 8,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography variant="body1">
                          {connection.name}
                        </Typography>
                        <Chip
                          label={
                            connection.status === "active"
                              ? "active"
                              : "inactive"
                          }
                          size="small"
                          color={
                            connection.status === "active" ? "success" : "error"
                          }
                          sx={{
                            height: 20,
                            "& .MuiChip-label": {
                              px: 1,
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      </Box>
                    }
                    primaryTypographyProps={{
                      component: "div",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Description Section */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            About
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {provider.description || "No description available"}
          </Typography>
        </Paper>
      </DialogContent>
    </Dialog>
  );
}
